import React from 'react';
import './Button.css';

export interface ButtonProps {
  /** The type/variant of the button */
  type?: 'primary' | 'secondary' | 'outline';
  /** The size of the button */
  size?: 'small' | 'medium' | 'large';
  /** Icon component to display */
  icon?: React.ReactNode;
  /** Position of the icon relative to text */
  iconPosition?: 'left' | 'right';
  /** Whether this is an icon-only button */
  isIconOnly?: boolean;
  /** Whether the button is disabled */
  disabled?: boolean;
  /** Whether the button is in loading state */
  isLoading?: boolean;
  /** Button contents */
  children: React.ReactNode;
  /** Optional click handler */
  onClick?: () => void;
  /** Additional CSS classes */
  className?: string;
}

/** Modern Button component with support for icons and multiple variants */
export const Button = ({
  type = 'primary',
  size = 'medium',
  icon,
  iconPosition = 'right',
  isIconOnly = false,
  disabled = false,
  isLoading = false,
  children,
  onClick,
  className = '',
  ...props
}: ButtonProps) => {
  const baseClass = 'crestron-button';
  const typeClass = `crestron-button--${type}`;
  const sizeClass = `crestron-button--${size}`;
  const iconOnlyClass = isIconOnly ? 'crestron-button--icon-only' : '';
  const disabledClass = disabled ? 'crestron-button--disabled' : '';
  const loadingClass = isLoading ? 'crestron-button--loading' : '';

  const buttonClasses = [
    baseClass,
    typeClass,
    sizeClass,
    iconOnlyClass,
    disabledClass,
    loadingClass,
    className
  ].filter(Boolean).join(' ');

  const handleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    if (disabled || isLoading) {
      e.preventDefault();
      return;
    }
    onClick?.(e);
  };

  return (
    <button
      type="button"
      className={buttonClasses}
      onClick={handleClick}
      disabled={disabled || isLoading}
      {...props}
    >
      {isLoading && (
        <span className="crestron-button__loader">
          <svg className="crestron-button__spinner" viewBox="0 0 24 24">
            <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" fill="none" strokeDasharray="31.416" strokeDashoffset="31.416">
              <animate attributeName="stroke-dasharray" dur="2s" values="0 31.416;15.708 15.708;0 31.416" repeatCount="indefinite" />
              <animate attributeName="stroke-dashoffset" dur="2s" values="0;-15.708;-31.416" repeatCount="indefinite" />
            </circle>
          </svg>
        </span>
      )}
      
      {!isLoading && (
        <>
          {icon && iconPosition === 'left' && !isIconOnly && (
            <span className="crestron-button__icon crestron-button__icon--left">
              {icon}
            </span>
          )}
          
          {!isIconOnly && (
            <span className="crestron-button__text">
              {children}
            </span>
          )}
          
          {icon && (iconPosition === 'right' || isIconOnly) && (
            <span className={`crestron-button__icon ${isIconOnly ? '' : 'crestron-button__icon--right'}`}>
              {icon}
            </span>
          )}
        </>
      )}
    </button>
  );
};
